import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_TAB,
  DEFAULT_PAGE_SIZE,
  TransactionTabType,
} from '../Transactions.constants';
import {
  mapPaymentToOperation,
  mapTransactionToOperation,
  mergeUserPaymentsResults,
  mergeUserTransactionsResults,
} from '../Transactions.helpers';
import { Operation } from '../Transactions.types';
import { useUserPayments } from './useUserPayments';
import { useUserTransactions } from './useUserTransactions';

export function useOperations(activeTab: TransactionTabType) {
  const { t } = useTranslation();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isAccountTab = activeTab === ACCOUNT_TAB;

  const paymentsQuery = useUserPayments({
    variables: { first: DEFAULT_PAGE_SIZE },
    skip: isAccountTab,
  });

  const transactionsQuery = useUserTransactions({
    variables: { first: DEFAULT_PAGE_SIZE },
    skip: !isAccountTab,
  });

  const isLoading = transactionsQuery.loading || paymentsQuery.loading;

  const operations = useMemo<Operation[]>(() => {
    if (isAccountTab) {
      return transactionsQuery.transactions.map(transaction =>
        mapTransactionToOperation(transaction, t)
      );
    }

    return paymentsQuery.payments.map(payment =>
      mapPaymentToOperation(payment, t)
    );
  }, [isAccountTab, paymentsQuery.payments, t, transactionsQuery.transactions]);

  const hasNextPage = isAccountTab
    ? transactionsQuery.hasNextPage
    : paymentsQuery.hasNextPage;

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !navigator.onLine) {
      return;
    }

    if (isAccountTab) {
      if (!transactionsQuery.hasNextPage || !transactionsQuery.endCursor) {
        return;
      }

      setIsLoadingMore(true);
      transactionsQuery
        .fetchMore({
          variables: { after: transactionsQuery.endCursor },
          updateQuery: mergeUserTransactionsResults,
        })
        .finally(() => setIsLoadingMore(false));

      return;
    }

    if (!paymentsQuery.hasNextPage || !paymentsQuery.endCursor) {
      return;
    }

    setIsLoadingMore(true);
    paymentsQuery
      .fetchMore({
        variables: { after: paymentsQuery.endCursor },
        updateQuery: mergeUserPaymentsResults,
      })
      .finally(() => setIsLoadingMore(false));
  }, [
    isAccountTab,
    isLoading,
    isLoadingMore,
    paymentsQuery,
    transactionsQuery,
  ]);

  return {
    operations,
    isLoading,
    isLoadingMore,
    hasNextPage,
    loadMore,
  };
}
